import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// React Native 앱과 동일한 26개 언어 지원
const supportedLanguages = [
  'en', 'ko', 'ja', 'zh', 'vi', 'es', 'fr', 'de', 'pt', 'id', 
  'th', 'tr', 'ru', 'it', 'el', 'hi', 'uk', 'pl', 'nl', 'hu', 
  'cs', 'sv', 'fi', 'no', 'ms', 'he'
];

// 언어별 표시명 (국기 이모지 포함)
const languageNames = {
  en: '🇺🇸 English',
  ko: '🇰🇷 한국어',
  ja: '🇯🇵 日本語',
  zh: '🇨🇳 中文',
  vi: '🇻🇳 Tiếng Việt',
  es: '🇪🇸 Español',
  fr: '🇫🇷 Français',
  de: '🇩🇪 Deutsch',
  pt: '🇵🇹 Português',
  id: '🇮🇩 Bahasa Indonesia',
  th: '🇹🇭 ไทย',
  tr: '🇹🇷 Türkçe',
  ru: '🇷🇺 Русский',
  it: '🇮🇹 Italiano',
  el: '🇬🇷 Ελληνικά',
  hi: '🇮🇳 हिन्दी',
  uk: '🇺🇦 Українська',
  pl: '🇵🇱 Polski',
  nl: '🇳🇱 Nederlands',
  hu: '🇭🇺 Magyar',
  cs: '🇨🇿 Čeština',
  sv: '🇸🇪 Svenska',
  fi: '🇫🇮 Suomi',
  no: '🇳🇴 Norsk',
  ms: '🇲🇾 Bahasa Melayu',
  he: '🇮🇱 עברית'
};

// 웹용 언어 감지기 (모바일 앱과 동일한 로직)
const webLanguageDetector = {
  type: 'languageDetector',
  async: true,
  detect: async (callback) => {
    console.log('🌐 Starting web language detection...');
    
    try {
      // Try to get saved language from localStorage
      const savedLanguage = localStorage.getItem('scanvcf-language');
      
      if (savedLanguage) {
        console.log('💾 Found saved language:', savedLanguage);
        callback(savedLanguage);
        return;
      }

      // Use browser language detection
      const browserLanguage = navigator.language || navigator.userLanguage;
      const detectedLanguage = browserLanguage.split('-')[0] || 'en';
      
      // Check if we support this language
      const language = supportedLanguages.includes(detectedLanguage) ? detectedLanguage : 'en';
      
      console.log('🌐 Browser language detected:', language);
      callback(language);
      
    } catch (error) {
      console.error('❌ Language detection error:', error);
      callback('en'); // Default fallback
    }
  },
  
  init: () => {
    console.log('🚀 Web language detector initialized');
  },
  
  cacheUserLanguage: async (language) => {
    try {
      localStorage.setItem('scanvcf-language', language);
      sessionStorage.setItem('scanvcf-language', language);
      console.log('💾 Language saved to storage:', language);
    } catch (error) {
      console.error('❌ Error saving language:', error);
    }
  }
};

// 웹용 리소스 로더 (모바일 앱과 동일한 구조)
const webResourceLoader = {
  type: 'backend',
  init: () => {
    console.log('🚀 Web resource loader initialized');
  },
  
  read: async (language, namespace, callback) => {
    try {
      console.log(`📖 Loading translation for: ${language}`);
      
      const response = await fetch(`/locales/${language}.json`);
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Translation loaded for ${language}:`, Object.keys(data).length, 'keys');
        callback(null, data);
      } else {
        console.warn(`⚠️ Translation not found for ${language}, falling back to English`);
        // Fallback to English
        const englishResponse = await fetch('/locales/en.json');
        if (englishResponse.ok) {
          const englishData = await englishResponse.json();
          callback(null, englishData);
        } else {
          callback(new Error(`Failed to load translations for ${language}`), null);
        }
      }
    } catch (error) {
      console.error(`❌ Error loading translation for ${language}:`, error);
      callback(error, null);
    }
  }
};

i18n
  .use(webResourceLoader)
  .use(webLanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: true,
    
    // Enhanced fallback configuration (모바일 앱과 동일)
    supportedLngs: supportedLanguages,
    nonExplicitSupportedLngs: false,
    
    // Key missing behavior - return key if translation not found
    missingKeyHandler: (lng, ns, key, fallbackValue) => {
      console.warn(`🔍 Missing translation: [${lng}] ${key} -> falling back to English`);
      return fallbackValue;
    },
    
    // Fallback configuration
    fallbackNS: false,
    saveMissing: true, // Save missing keys in development
    
    interpolation: {
      escapeValue: false,
    },
    
    // Improved JSON compatibility and return behavior (모바일 앱과 동일)
    returnObjects: true,
    returnedObjectHandler: (key, value, options) => {
      return value;
    },
    
    compatibilityJSON: 'v4',
    
    react: {
      useSuspense: false,
    },
    
    // Enhanced debug for development
    debug: true,
    
    // Additional fallback options
    cleanCode: true,
    
    // Load namespace behavior (모바일 앱과 동일)
    defaultNS: 'translation',
    ns: ['translation'],
    
    // Key separator for nested objects
    keySeparator: '.',
    nsSeparator: ':',
  });

// Log when i18n is initialized
i18n.on('initialized', (options) => {
  console.log('🎉 Web i18n initialized with language:', i18n.language);
});

// Log when language changes
i18n.on('languageChanged', (lng) => {
  console.log('🔄 Web language changed to:', lng);
  
  // Save to storage
  webLanguageDetector.cacheUserLanguage(lng);
});

// Function to force change language and save (모바일 앱과 동일)
export const forceChangeLanguage = async (lng) => {
  try {
    console.log('🔧 Force changing web language to:', lng);
    
    // Check if the language is already the current language
    if (i18n.language === lng) {
      console.log('✅ Language is already set to:', lng);
      return true;
    }
    
    // Change language
    await i18n.changeLanguage(lng);
    
    console.log('✅ Web language force changed:', lng);
    
    // Force a small delay to ensure all components have time to re-render
    await new Promise(resolve => setTimeout(resolve, 100));
    
    return true;
  } catch (error) {
    console.error('❌ Error force changing web language:', error);
    throw error;
  }
};

// Enhanced translation function with guaranteed fallback to English (모바일 앱과 동일)
export const getTranslation = (key, options = {}) => {
  try {
    // First try current language
    const currentTranslation = i18n.t(key, { ...options, lng: i18n.language });
    
    // Check if we got a valid translation (not the key itself)
    if (currentTranslation && currentTranslation !== key && !currentTranslation.startsWith('common.')) {
      return currentTranslation;
    }
    
    // If current language failed, force English fallback
    console.warn(`🔄 Using English fallback for key: ${key}`);
    const englishTranslation = i18n.t(key, { ...options, lng: 'en' });
    
    // If even English doesn't have it, return the key with a warning
    if (englishTranslation === key) {
      console.error(`❌ Missing translation in English: ${key}`);
      return key; // Return key as last resort
    }
    
    return englishTranslation;
  } catch (error) {
    console.error(`❌ Translation error for key ${key}:`, error);
    return key;
  }
};

// 언어 이름과 코드를 내보내기
export { languageNames, supportedLanguages };

export default i18n; 